import { isDailyAbsiteEnabled } from '../constants/routeMeta';
import { AD_CLICK_URL_ONLY_KEYS, extractAttributionParams, setUrlOnlyParams } from './attribution';

const DAILY_PATH = '/daily-absite-question';
const LOGIN_PATH = '/login';
const TOUCH_PATTERN = /^dqt_[0-9a-f]{64}$/;
const GRANT_PATTERN = /^dqg_[0-9a-f]{64}$/;
const REVISION_HASH_PATTERN = /^[0-9a-f]{64}$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
// The campaign keys a cleaned Daily address keeps (ads attribution contract,
// 2026-10-20): Google's conversion linker reads gclid from the address. The
// ad click ids (AD_CLICK_URL_ONLY_KEYS) follow them, address only. `ref` and
// every other key are still dropped.
const CAMPAIGN_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid',
];

export { isDailyAbsiteEnabled };

const canonicalRoute = (pathname = '') => {
  const withoutTrailingSlash = (pathname || '').replace(/\/+$/, '');
  return (withoutTrailingSlash || '/').toLowerCase();
};

export const isOpaqueDailyTouchId = (value) => TOUCH_PATTERN.test(value || '');
export const isOpaqueDailyGrantId = (value) => GRANT_PATTERN.test(value || '');

const isIsoDate = (value) => {
  const match = DATE_PATTERN.exec(value || '');
  if (!match) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

const readImmutableIdentity = (params) => {
  const date = params.get('date');
  const revisionId = params.get('revisionId');
  const revisionHash = params.get('revisionHash');
  if (!isIsoDate(date)
      || !REVISION_HASH_PATTERN.test(revisionHash || '')
      || revisionId !== `dqr_${revisionHash}`) {
    return {};
  }
  return { date, revisionId, revisionHash };
};

const readOpaqueIds = (search = '') => {
  const params = new URLSearchParams(search);
  const touchId = params.get('touch');
  const grantId = params.get('grant');
  return {
    ...readImmutableIdentity(params),
    ...(isOpaqueDailyTouchId(touchId) ? { touchId } : {}),
    ...(isOpaqueDailyGrantId(grantId) ? { grantId } : {}),
  };
};

export const buildOpaqueDailyPagePath = ({
  date, revisionId, revisionHash, touchId, grantId,
} = {}) => {
  const params = new URLSearchParams();
  const identity = readImmutableIdentity(new URLSearchParams({
    ...(date ? { date } : {}),
    ...(revisionId ? { revisionId } : {}),
    ...(revisionHash ? { revisionHash } : {}),
  }));
  if (identity.date) {
    params.set('date', identity.date);
    params.set('revisionId', identity.revisionId);
    params.set('revisionHash', identity.revisionHash);
  }
  if (isOpaqueDailyTouchId(touchId)) params.set('touch', touchId);
  if (isOpaqueDailyGrantId(grantId)) params.set('grant', grantId);
  const query = params.toString();
  return `${DAILY_PATH}${query ? `?${query}` : ''}`;
};

// Appends the campaign keys found in `search` to an opaque Daily path, after
// its opaque state, with the attribution store's own trim and 500-character
// cap, then the ad click ids exactly as sent.
export const appendDailyCampaignParams = (path, search = '') => {
  const attribution = extractAttributionParams(search);
  const campaign = new URLSearchParams();
  CAMPAIGN_KEYS.forEach((key) => {
    if (attribution[key]) campaign.set(key, attribution[key]);
  });
  setUrlOnlyParams(campaign, search, AD_CLICK_URL_ONLY_KEYS);
  const query = campaign.toString();
  if (!query) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${query}`;
};

// The Daily landing, or its login return (/login?next=/daily-absite-question).
export const isOpaqueDailyEntry = (location = window.location) => {
  const route = canonicalRoute(location?.pathname);
  if (route === DAILY_PATH) return true;
  return route === LOGIN_PATH
    && canonicalRoute(new URLSearchParams(location?.search).get('next')) === DAILY_PATH;
};

export const sanitizeOpaqueDailyEntry = ({
  location = window.location,
  history = window.history,
} = {}) => {
  if (!isDailyAbsiteEnabled()) return {};
  if (!isOpaqueDailyEntry(location)) return {};
  const route = canonicalRoute(location.pathname);

  const opaqueIds = readOpaqueIds(location.search);
  const params = new URLSearchParams(location.search);
  const opaquePath = route === LOGIN_PATH
    ? buildOpaqueDailyLoginPath({
      auth: params.get('auth'),
      ...opaqueIds,
    })
    : buildOpaqueDailyPagePath(opaqueIds);
  const cleanPath = appendDailyCampaignParams(opaquePath, location.search);
  const currentPath = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  if (currentPath !== cleanPath) {
    history.replaceState(history.state, '', cleanPath);
  }
  return opaqueIds;
};

export const buildOpaqueDailyLoginPath = ({
  auth, date, revisionId, revisionHash, touchId, grantId,
} = {}) => {
  const params = new URLSearchParams();
  if (auth === 'google' || auth === 'phone') params.set('auth', auth);
  params.set('next', DAILY_PATH);
  const identity = readImmutableIdentity(new URLSearchParams({
    ...(date ? { date } : {}),
    ...(revisionId ? { revisionId } : {}),
    ...(revisionHash ? { revisionHash } : {}),
  }));
  if (identity.date) {
    params.set('date', identity.date);
    params.set('revisionId', identity.revisionId);
    params.set('revisionHash', identity.revisionHash);
  }
  if (isOpaqueDailyTouchId(touchId)) params.set('touch', touchId);
  if (isOpaqueDailyGrantId(grantId)) params.set('grant', grantId);
  return `/login?${params.toString()}`;
};

// An expired or withdrawn daily-post QLink: the API marks its 404 `expired`
// only for a link that existed, and only to a request carrying that link's own
// share token (QLearn-service QLinkController). Those visitors go to the Daily
// page (today's case, answerable signed out), tagged as this campaign. Built
// from constants: nothing from the API response or the old link rides along
// except the page's platform label, and only one of the four social platforms
// the server's own redirect allows (QLinkManager.SOCIAL_PLATFORMS).
// The Daily landing saves the tags first-party (store.js) and keeps the utm
// keys in its address; `ref` was captured at the QLink landing.
export const QLINK_EXPIRED_CAMPAIGN = Object.freeze({
  utm_source: 'qlink',
  utm_medium: 'social',
  utm_campaign: 'qlink_expired',
});
const QLINK_EXPIRED_PLATFORMS = new Set(['twitter', 'facebook', 'linkedin', 'instagram']);

export const qlinkExpiredDailyPath = (search) => {
  const params = new URLSearchParams();
  Object.entries(QLINK_EXPIRED_CAMPAIGN).forEach(([key, value]) => params.set(key, value));
  let platform = '';
  try {
    platform = (new URLSearchParams(search || '').get('platform') || '').trim().toLowerCase();
  } catch (_) {
    platform = '';
  }
  if (QLINK_EXPIRED_PLATFORMS.has(platform)) {
    params.set('platform', platform);
  }
  return `${DAILY_PATH}?${params.toString()}`;
};

export const getOpaqueDailyReturnPath = (search = '') => {
  if (!isDailyAbsiteEnabled()) return undefined;
  const params = new URLSearchParams(search);
  if (canonicalRoute(params.get('next')) !== DAILY_PATH) return undefined;
  const opaqueIds = readOpaqueIds(search);
  if (!opaqueIds.date && !opaqueIds.touchId && !opaqueIds.grantId) return undefined;
  return buildOpaqueDailyPagePath(opaqueIds);
};
